import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ReactComponent as BurgerIcon } from '../../assets/icons/burger.svg';

export interface BurgerMenuItem {
  key: string;
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

interface Props {
  items: BurgerMenuItem[];
}

const BurgerMenu = ({ items }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (item: BurgerMenuItem) => {
    if (item.active) {
      return;
    }

    setIsOpen(false);
    item.onClick();
  };

  return (
    <div className='burger-menu' ref={containerRef}>
      <button
        type='button'
        className='burger-menu__trigger'
        aria-haspopup='menu'
        aria-expanded={isOpen}
        aria-label='Menu'
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <BurgerIcon className='burger-menu__icon' />
      </button>
      {isOpen && (
        <ul className='burger-menu__dropdown' role='menu'>
          {items.map((item) => (
            <li key={item.key}>
              <button
                type='button'
                role='menuitem'
                disabled={item.active}
                aria-disabled={item.active}
                className={classNames('burger-menu__option', { 'burger-menu__option--disabled': item.active })}
                onClick={() => handleSelect(item)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BurgerMenu;
